import * as React from 'react'
import {
  GridColumnVisibilityModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid-pro'
import {
  ageValueFormatter,
  campaignValueFormatter,
  GridEmailLink,
  GridLocationLink,
  GridPhoneLink,
  salesFunnelStageValueFormatter,
  salesFunnelValueFormatter,
  salesTeamValueFormatter,
  SortConfig,
  sourceValueFormatter,
  userValueFormatter,
} from '@user/components/xGrid'

import Link from '@mui/material/Link'
import { bulkErrorColumn } from '@user/components/xGrid/'
import { LocationsListViewWrapper } from '@user/components/EntityCards/Locations'
import { referralDiscountCodeFormatter } from '@user/components/xGrid/valueFormatters/referralDiscountCode'
import {
  currencyValueFormatter,
  divisionValueFormatter,
  locationTypeValueFormatter,
} from '@user/components/EntityCards/Locations/valueFormaters'
import {
  ENTITY_TYPES,
  getFormattedAddress,
  IServiceDTO,
} from '@sales-center/common'
import { isMatchedEntity } from '@user/utils/isMatchEntity'
import { referralValueChecker } from '@user/components/xGrid/valueFormatters/referralValueFormatter'
import { ITargetPest } from '@user/store/targetPests'
import ListViewAddressMapLink from './ListViewAddressMapLink'
import Typography from '@mui/material/Typography'

export const leadListColumns = (
  allTargetPests: ITargetPest[],
  supportLInks: boolean = true,
  knockStatusEnabled: boolean = false,
) => {
  return [
    { ...bulkErrorColumn },
    {
      field: 'age',
      headerName: 'Age',
      valueFormatter: ageValueFormatter,
    },
    {
      field: 'id',
      headerName: 'Lead Created Date',
      valueGetter: ({ row }) => row.createdDate,
      type: 'date',
      width: 145,
    },
    {
      field: 'leadName',
      headerName: 'Lead Name',
      width: 150,
      renderCell: ({ value, row }) =>
        supportLInks ? (
          <Link
            href={`#/leads/${row.id}`}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {value}
          </Link>
        ) : (
          <Typography>{value}</Typography>
        ),
    },
    {
      field: 'externalBillToCode',
      headerName: 'Bill-to Location Number',
      width: 170,
      renderCell: ({ row }) => {
        return (
          row.externalBillToId && (
            <GridLocationLink
              locationId={row.externalBillToId}
              locationCode={row.externalBillToCode}
              variant="billto"
            />
          )
        )
      },
    },
    {
      field: 'externalLocationCode',
      headerName: 'Service Location Number',
      width: 170,
      renderCell: ({ row }) => {
        return row.serviceLocationsCount > 1 ? (
          isMatchedEntity(row.externalLocationId, row.externalLocationCode) && (
            <LocationsListViewWrapper
              field="externalLocationCode"
              row={row}
              entityType={ENTITY_TYPES.Lead}
            />
          )
        ) : (
          <GridLocationLink
            locationId={row.externalLocationId}
            locationCode={row.externalLocationCode}
            variant="service"
          />
        )
      },
    },

    {
      field: 'divisionId',
      headerName: 'Service Location Division',
      width: 170,
      renderCell: ({ row }) =>
        supportLInks && row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="divisionId"
            row={row}
            entityType={ENTITY_TYPES.Lead}
          />
        ) : (
          divisionValueFormatter(row.serviceLocation.divisionId)
        ),
    },

    {
      field: 'locationTypeId',
      headerName: 'Service Location Type',
      width: 170,
      renderCell: ({ row }) =>
        supportLInks && row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="locationTypeId"
            row={row}
            entityType={ENTITY_TYPES.Lead}
          />
        ) : (
          locationTypeValueFormatter(row.serviceLocation.locationTypeId)
        ),
    },

    {
      field: 'nextActionDate',
      headerName: 'Next Action Date',
      type: 'date',
      width: 165,
    },
    {
      field: 'assignee/firstName',
      headerName: 'Owner',
      valueGetter: ({ row }) => row.assignee,
      valueFormatter: (params: GridValueFormatterParams) =>
        userValueFormatter(params),
      width: 110,
    },
    {
      field: 'primaryContactSearchAddress',
      headerName: 'Primary Contact Address',
      renderCell: ({ row }) =>
        getFormattedAddress(
          row.primaryContact.address,
          row.primaryContact.address2,
          row.primaryContact.city,
          row.primaryContact.state,
          row.primaryContact.postalCode,
        ),
      width: 130,
      sortable: false,
    },
    {
      field: 'primaryContactSearchPrimaryEmail',
      headerName: 'Primary Contact Email',
      renderCell: ({ row }) => (
        <GridEmailLink value={row.primaryContact.primaryEmailAddress} />
      ),
      width: 130,
      sortable: false,
    },
    {
      field: 'primaryContactSearchPhone',
      headerName: 'Primary Contact Phone',
      renderCell: ({ row }) => (
        <GridPhoneLink
          phone={row.primaryContact.phone}
          ext={row.primaryContact.phoneExt}
        />
      ),
      width: 130,
      sortable: false,
    },
    {
      field: 'closedDate',
      headerName: 'Disqualified Date',
      type: 'date',
      width: 165,
    },
    {
      field: 'salesFunnelId',
      headerName: 'Sales Funnel',
      valueFormatter: salesFunnelValueFormatter,
      width: 145,
    },
    {
      field: 'address',
      headerName: 'Service Location Address',
      width: 350,
      sortable: false,
      renderCell: ({ row }) => {
        if (!supportLInks) {
          const formattedAddress = getFormattedAddress(
            row.serviceLocation.lineOne,
            row.serviceLocation.lineTwo,
            row.serviceLocation.city,
            row.serviceLocation.state,
            row.serviceLocation.postalCode,
          )

          return <Typography>{formattedAddress}</Typography>
        }

        return row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="address"
            row={row}
            entityType={ENTITY_TYPES.Lead}
          />
        ) : (
          <ListViewAddressMapLink
            row={{ ...row, entityType: ENTITY_TYPES.Lead }}
          />
        )
      },
    },
    {
      field: 'salesFunnelStageId',
      headerName: 'Status',
      valueFormatter: salesFunnelStageValueFormatter,
      width: 110,
    },
    {
      field: 'totalLeadValue',
      headerName: 'Total Value',
      valueFormatter: currencyValueFormatter,
      width: 140,
      renderCell: ({ row }) =>
        supportLInks && row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="totalValue"
            row={row}
            listViewValue={currencyValueFormatter(row.totalLeadValue)}
            entityType={ENTITY_TYPES.Lead}
          />
        ) : (
          currencyValueFormatter(row.totalLeadValue)
        ),
    },
    {
      field: 'sourceId',
      headerName: 'Lead Source',
      valueFormatter: sourceValueFormatter,
      width: 140,
    },
    {
      field: 'campaignId',
      headerName: 'Campaign',
      valueFormatter: campaignValueFormatter,
      width: 130,
    },
    {
      field: 'billToDivisionId',
      headerName: 'Bill-to Division',
      width: 120,
      renderCell: ({ row }) => divisionValueFormatter(row.billToDivisionId),
    },
    {
      field: 'billToLocationTypeId',
      headerName: 'Bill-to Location Type',
      width: 150,
      renderCell: ({ row }) =>
        locationTypeValueFormatter(row.billToLocationTypeId),
    },
    {
      field: 'createdBy/firstName',
      headerName: 'Lead Created By',
      valueGetter: ({ row }) => row.createdBy,
      valueFormatter: userValueFormatter,
      width: 130,
    },
    {
      field: 'services',
      headerName: 'Services',
      valueFormatter: ({ value }) =>
        (value as IServiceDTO[])?.map(({ name }) => name).join(', '),
      sortable: false,
    },
    {
      field: 'salesTeamId',
      headerName: 'Sales Team',
      valueFormatter: salesTeamValueFormatter,
      width: 140,
    },
    {
      field: 'branch',
      headerName: 'Branch',
      width: 120,
    },
    {
      field: 'initialValue',
      headerName: 'Initial Value',
      width: 110,
      renderCell: ({ row }) =>
        supportLInks && row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="initialValue"
            row={row}
            listViewValue={currencyValueFormatter(row.initialValue)}
            entityType={ENTITY_TYPES.Lead}
          />
        ) : (
          currencyValueFormatter(row.initialValue)
        ),
    },
    {
      field: 'recurringValue',
      headerName: 'Annual Value',
      valueFormatter: currencyValueFormatter,
      width: 140,
      renderCell: ({ row }) =>
        supportLInks && row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="recurringValue"
            row={row}
            listViewValue={currencyValueFormatter(row.recurringValue)}
            entityType={ENTITY_TYPES.Lead}
          />
        ) : (
          currencyValueFormatter(row.recurringValue)
        ),
    },
    {
      field: 'referralDiscountCode',
      headerName: 'Referral Discount Code',
      valueFormatter: referralDiscountCodeFormatter,
      width: 110,
    },
    {
      field: 'primaryContactSearchName',
      headerName: 'Primary Contact Name',
      width: 130,
      sortable: false,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 110,
    },
    {
      field: 'disqualifiedReason',
      headerName: 'Disqualified Reason',
      width: 170,
    },
    {
      field: 'targetPestIds',
      headerName: 'Target Pests',
      sortable: false,
      valueFormatter: ({ value }) => {
        return value
          .map((id) => allTargetPests?.find((pest) => pest.id === id)?.name)
          .filter(Boolean)
          .join(', ')
      },
      width: 170,
    },
    {
      field: 'submittedBy',
      headerName: 'Submitted by',
      width: 170,
    },
    {
      field: 'billToAddress',
      headerName: 'Bill-To Address',
      width: 170,
      renderCell: ({ row }) => {
        const formattedAddress = getFormattedAddress(
          row.billToContact.street1,
          row.billToContact.street2,
          row.billToContact.city,
          row.billToContact.stateAbbreviation,
          row.billToContact.postalCode,
        )

        return <Typography>{formattedAddress}</Typography>
      },
    },
    {
      field: 'customerReferralLocationCode',
      headerName: 'Customer Referral',
      width: 150,
      renderCell: ({ row }) => {
        const {
          referralType,
          customerReferralLocationCode,
          customerReferralLocationId,
        } = row

        const checkedValue = referralValueChecker(
          referralType,
          'Customer',
          customerReferralLocationCode,
        )
        if (!checkedValue || !supportLInks) return checkedValue

        return (
          <GridLocationLink
            locationId={customerReferralLocationId}
            locationCode={checkedValue}
            variant="billto"
          />
        )
      },
    },
    {
      field: 'employeeReferral',
      headerName: 'Employee Referral',
      width: 150,
      renderCell: ({ row }) => {
        const { referralType, employeeReferral } = row
        return referralValueChecker(referralType, 'Employee', employeeReferral)
      },
    },
    {
      field: 'otherReferral',
      headerName: 'Other Referral',
      width: 150,
      renderCell: ({ row }) => {
        const { referralType, otherReferral } = row
        return referralValueChecker(referralType, 'Other', otherReferral)
      },
    },
    ...(knockStatusEnabled
      ? [
          {
            field: 'knockStatus',
            headerName: 'Current Knock Status',
            width: 200,
          },
          {
            field: 'knockStatusReason',
            headerName: 'Knock Status Reason',
            width: 180,
          },
        ]
      : []),
  ]
}

export const lockedLeadColumns = ['leadName']

export const defaultLeadColVis = leadListColumns(
  [],
).reduce<GridColumnVisibilityModel>(
  (acc, { field }) => {
    if (acc[field] === undefined) {
      return { ...acc, [field]: true }
    } else return acc
  },
  {
    // columns hidden by default
    sourceId: false,
    campaignId: false,
    billToDivisionId: false,
    billToLocationTypeId: false,
    services: false,
    salesTeamId: false,
    branch: false,
    initialValue: false,
    recurringValue: false,
    referralLocationCode: false,
    referralDiscountCode: false,
    primaryContactSearchName: false,
    id: false,
    externalBillToCode: false,
    externalLocationCode: false,
    locationTypeId: false,
    divisionId: false,
    closedDate: false,
    'primaryContact/primaryEmailAddress': false,
    'primaryContact/phone': false,
    'primaryContact/address': false,
    type: false,
    disqualifiedReason: false,
    submittedBy: false,
    targetPestIds: false,
    billToAddress: false,
    customerReferralLocationCode: false,
    employeeReferral: false,
    otherReferral: false,
  },
)

export const defaultLeadColumns = leadListColumns([]).map((c) => c.field)

export const leadSortConfig: SortConfig = {
  createdDate: { column: 'id', tiebreakers: ['id'] },
}
